/**
 * Mapping a planet's longitude onto the price axis, and stacking the result into octaves.
 *
 * <h2>What the overlay actually carries</h2>
 * By the time an overlay reaches the chart its values are already PRICES, not degrees. App.js
 * unwraps the longitude series and multiplies by `pricePer360 / 360` before storing it. That is the
 * single fact the renderer has to get right, and getting it wrong is subtle rather than obvious:
 * treating a price as though it were still degrees produces a line that looks plausible, moves with
 * the planet, and is wrong.
 *
 * The renderer used to do exactly that — `(value % 360) * pricePer360 / 360` — which scaled by
 * `pricePer360/360` a second time and took a modulus at 360 PRICE units. With pricePer360 = 200 and
 * Venus, the plotted sawtooth repeated every 405 days against a 225-day orbit, and dropped the full
 * height of a band at a moment when Venus was at 288 degrees: nowhere near Aries, and nothing to do
 * with the planet at all. Reducing the price modulo `pricePer360` instead gives back exactly the
 * fractional position in the turn, so one band is one revolution.
 */

/** Positive modulus. A retrograde run can unwrap below zero, where JS `%` returns a negative. */
function mod(value, m) {
  if (!(m > 0)) return 0;
  return ((value % m) + m) % m;
}

/**
 * Where a longitude-derived price sits inside one octave, before the octave offset is added.
 *
 * <h2>Folded to 0-180</h2>
 * A longitude at or below 180 degrees plots as itself; above 180 it plots as {@code 360 - x}. So
 * 270 degrees draws at the same height as 90, and the band reads as an angular DISTANCE from
 * Aries rather than as a position around the circle.
 *
 * <p><b>The fold happens in price units, and that is exact rather than approximate.</b> The value
 * arriving here is already {@code degrees * pricePer360 / 360}, a linear map, so
 * {@code degrees > 180} is precisely {@code price > pricePer360 / 2} and {@code 360 - degrees}
 * corresponds precisely to {@code pricePer360 - price}. Folding after the modulus therefore gives
 * the same answer as folding the degrees would, without needing the degrees back.
 *
 * <h2>Two consequences worth knowing</h2>
 * The output range halves, to {@code [0, pricePer360 / 2]}. `pricePer360` still means "price units
 * per full revolution" and still sets the octave height, so a folded line occupies the LOWER HALF
 * of each band and the upper half is empty. That follows from folding while keeping the band
 * height unchanged, which is what "spatial constraints remain identical" asks for.
 *
 * <p>And the sawtooth becomes a triangle wave: the value climbs to the fold and comes back down
 * instead of dropping. It is now continuous everywhere, so the revolution "cliff" that
 * {@link bandSeries} inserts a null for can no longer occur — its guard needs a jump greater than
 * half a band, and half a band is now the entire range.
 *
 * @param {number} value    the overlay's value: an unwrapped longitude already scaled to price
 * @param {number} pricePer360 price units spanned by one full revolution
 * @returns {number} a value in [0, pricePer360 / 2]
 */
export function positionInBand(value, pricePer360) {
  const around = mod(value, pricePer360);
  const half = pricePer360 / 2;
  return around <= half ? around : pricePer360 - around;
}

/**
 * The octave indices needed to cover a price range.
 *
 * Each octave is one full revolution of the planet, `pricePer360` tall, stacked from `basePrice`.
 * Enough of them are returned to cover `basePrice` up to `topPrice`, so a chart at SPY's $500 gets
 * the whole axis without the user adding one instance per band by hand.
 *
 * @returns {number[]} octave indices, ascending, always at least [0]
 */
export function autoOctaves(basePrice, topPrice, pricePer360) {
  if (!(pricePer360 > 0) || !Number.isFinite(basePrice) || !Number.isFinite(topPrice)) return [0];
  const span = topPrice - basePrice;
  if (!(span > 0)) return [0];
  // +1 so the topmost band covers the axis rather than stopping just under it.
  const count = Math.ceil(span / pricePer360) + 1;
  return Array.from({ length: count }, (_, k) => k);
}

/**
 * The octave indices a user asked for explicitly, as an inclusive range.
 *
 * Falls back to the automatic set when the range is missing or nonsensical — a reversed or
 * unparseable range should not silently plot nothing.
 */
export function explicitOctaves(nStart, nEnd) {
  // A cleared number field gives '', and Number('') is 0 — finite, so it would quietly be read as
  // octave zero. An empty box means "not specified", which falls back to auto-fit.
  const blank = v => v === null || v === undefined || String(v).trim() === '';
  if (blank(nStart) || blank(nEnd)) return null;

  const from = Number(nStart);
  const to = Number(nEnd);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
  const lo = Math.min(Math.trunc(from), Math.trunc(to));
  const hi = Math.max(Math.trunc(from), Math.trunc(to));
  // A guard against a typo asking for thousands of series.
  if (hi - lo > 50) return null;
  return Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
}

/**
 * The octaves to draw, honouring the overlay's auto-fit setting.
 *
 * Auto-fit is the default: an overlay saved before these controls existed carries neither flag and
 * should keep behaving as it did, which was to cover the chart automatically.
 */
export function octavesFor(overlay, basePrice, topPrice, pricePer360) {
  const autoFit = overlay?.autoFitOctaves !== false;
  if (!autoFit) {
    const explicit = explicitOctaves(overlay.octaveStart, overlay.octaveEnd);
    if (explicit) return explicit;
  }
  return autoOctaves(basePrice, topPrice, pricePer360);
}

/**
 * One octave's series, with the wrap at Aries left as a gap rather than drawn.
 *
 * A modular band has to reset once per revolution: the planet leaves 359.99 degrees and re-enters
 * at 0, so the price jumps from the top of the band to the bottom. Joining those two points draws a
 * vertical line the full height of the band straight down the chart — the "cliff". ECharts breaks a
 * line wherever a point's value is null, so emitting a null at the reset gives one clean path per
 * revolution instead.
 *
 * The gap replaces nothing: it is inserted between the last point of one revolution and the first
 * of the next, so no bar loses its value.
 *
 * @param {string[]} dates    x values, one per point
 * @param {number[]} values   overlay values (unwrapped longitude already scaled to price)
 * @param {number}   pricePer360
 * @param {number}   basePrice bottom of the lowest band
 * @param {number}   octave    which band, 0 being the lowest
 * @returns {Array} ECharts [x, y] pairs, with [x, null] at each revolution boundary
 */
export function bandSeries(dates, values, pricePer360, basePrice, octave) {
  const out = [];
  const offset = basePrice + octave * pricePer360;
  let previous = null;

  for (let i = 0; i < dates.length; i++) {
    const value = values[i];
    if (!Number.isFinite(value)) {
      out.push([dates[i], null]);
      previous = null;
      continue;
    }
    const position = positionInBand(value, pricePer360);

    // A drop of more than half a band between neighbouring points is the wrap, not motion: no
    // planet covers half a revolution in one day. Retrograde motion crossing the boundary the
    // other way jumps upward by the same amount, and breaks the line for the same reason.
    if (previous !== null && Math.abs(position - previous) > pricePer360 / 2) {
      out.push([dates[i], null]);
    }
    out.push([dates[i], offset + position]);
    previous = position;
  }
  return out;
}

/**
 * Headroom above the highest bar, so the topmost octave has somewhere to sit.
 *
 * A planetary line is a support/resistance level, and the levels immediately ABOVE the market are
 * the ones a trader is watching for. Fitting octaves to the exact high leaves none of them drawn,
 * because the band covering that price starts below it.
 */
export const BOUNDS_HEADROOM = 1.10;

/**
 * The price range planetary octaves are fitted to: the chart's lowest low up to its highest high
 * plus {@link BOUNDS_HEADROOM}.
 *
 * <h2>Computed from the whole dataset, once</h2>
 * From `barData`, which is the full loaded series — zooming does not change it, and ECharts handles
 * dataZoom internally without re-running this. So the octave set is fixed for the life of the data
 * and a zoom cannot add or drop a band underfoot. That is a property of where this is called from,
 * not something re-derived per frame, and it is why the levels stay put while the axis moves.
 *
 * <h2>The high really is index 3</h2>
 * `ohlc` rows are `[open, close, low, high]` — ECharts candlestick order, not the order the name
 * implies. This read `bar[1]` for the high, which is the CLOSE, and labelled it `// high`. On any
 * series that closes below its high — all of them — the fitted range stopped short, so the top
 * octave was missing exactly where price had most recently traded.
 *
 * Written as a loop rather than `Math.min(...arr)`: these series run to five figures of bars
 * (AAPL is ~11,500) and spreading an array that size into an argument list is a stack overflow
 * waiting for a longer history.
 */
export function planetaryLineBounds(barData) {
  const rows = barData?.ohlc;
  if (!Array.isArray(rows) || rows.length === 0) return { low: 0, high: 0 };

  let low = Infinity;
  let high = -Infinity;
  for (const bar of rows) {
    if (!bar) continue;
    const barLow = bar[2];
    const barHigh = bar[3];
    if (Number.isFinite(barLow) && barLow < low) low = barLow;
    if (Number.isFinite(barHigh) && barHigh > high) high = barHigh;
  }
  if (!Number.isFinite(low) || !Number.isFinite(high)) return { low: 0, high: 0 };
  return { low, high: high * BOUNDS_HEADROOM };
}
